/* Document Preview Feature - Updated 2025-10-20 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  PenTool, 
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentManager = ({ clientId, clientName }) => {
  const [documents, setDocuments] = useState([]);
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    files: [],
    file_type: "general"
  });
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewDocument, setPreviewDocument] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const documentTypes = {
    facture_elec: "Facture Électricité",
    facture_gaz: "Facture Gaz", 
    acd_signe: "ACD Signé",
    contrat: "Contrat",
    general: "Document Général"
  };

  useEffect(() => {
    if (clientId) {
      fetchDocuments();
      fetchSignatureRequests();
    }
  }, [clientId]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatureRequests = async () => {
    try {
      const response = await axios.get(`${API}/signature-requests`);
      setSignatureRequests(response.data.filter(req => req.client_id === clientId));
    } catch (error) {
      console.error("Error fetching signature requests:", error);
    }
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFilesSelected(droppedFiles);
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFilesSelected(selectedFiles);
  };

  const handleFilesSelected = (files) => {
    setUploadData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeFile = (index) => {
    setUploadData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (uploadData.files.length === 0) {
      toast.error("Veuillez sélectionner au moins un fichier");
      return;
    }

    try {
      setUploadProgress({});
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < uploadData.files.length; i++) {
        const file = uploadData.files[i];
        
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('file_type', uploadData.file_type);

          await axios.post(`${API}/clients/${clientId}/documents`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({
                ...prev,
                [i]: percentCompleted
              }));
            }
          });
          
          successCount++;
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} document(s) téléchargé(s) avec succès`);
        fetchDocuments();
      }
      if (failCount > 0) {
        toast.error(`${failCount} document(s) ont échoué`);
      }

      setShowUploadDialog(false);
      setUploadData({ files: [], file_type: "general" });
      setUploadProgress({});
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce document ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/clients/${clientId}/documents/${documentId}`);
      toast.success("Document supprimé avec succès");
      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const isPreviewSupported = (filename) => {
    const supportedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif'];
    const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
    return supportedExtensions.includes(extension);
  };

  const getFileType = (filename) => {
    const extension = filename.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
    if (extension === '.pdf') return 'pdf';
    if (['.jpg', '.jpeg', '.png', '.heic', '.heif'].includes(extension)) return 'image';
    return 'unsupported';
  };

  const handlePreview = async (doc) => {
    const fileType = getFileType(doc.original_filename);
    const isSupported = isPreviewSupported(doc.original_filename);
    
    setPreviewDocument({
      id: doc.id,
      filename: doc.original_filename,
      fileType: fileType,
      isSupported: isSupported
    });
    setZoomLevel(100);
    setPreviewUrl(null);
    
    // If supported, fetch the document with authentication
    if (isSupported) {
      setLoadingPreview(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/documents/${doc.id}/download`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Get the blob with correct MIME type
        const blob = await response.blob();
        
        // Determine MIME type based on file extension
        let mimeType = blob.type;
        if (!mimeType || mimeType === 'application/octet-stream') {
          const extension = doc.original_filename.toLowerCase().match(/\.[^.]+$/)?.[0];
          if (extension === '.pdf') {
            mimeType = 'application/pdf';
          } else if (['.jpg', '.jpeg'].includes(extension)) {
            mimeType = 'image/jpeg';
          } else if (extension === '.png') {
            mimeType = 'image/png';
          }
        }
        
        // Create blob with correct type
        const typedBlob = new Blob([blob], { type: mimeType });
        const blobUrl = window.URL.createObjectURL(typedBlob);
        setPreviewUrl(blobUrl);
      } catch (error) {
        console.error("Error loading preview:", error);
        toast.error("Erreur lors du chargement de la prévisualisation");
      } finally {
        setLoadingPreview(false);
      }
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewDocument(null);
    setPreviewUrl(null);
    setZoomLevel(100);
  };

  const handleSendForSignature = async (documentId, provider = "yousign") => {
    try {
      const response = await axios.post(`${API}/documents/${documentId}/sign`, {
        provider: provider
      });
      
      toast.success("Document envoyé pour signature");
      
      // Show signing URL (in real implementation, this would be sent by email)
      if (response.data.signing_url) {
        toast.info(`Lien de signature: ${response.data.signing_url}`);
      }
      
      fetchDocuments();
      fetchSignatureRequests();
    } catch (error) {
      console.error("Error sending for signature:", error);
      toast.error("Erreur lors de l'envoi pour signature");
    }
  };

  const getSignatureStatus = (documentId) => {
    const doc = documents.find(d => d.id === documentId);
    if (!doc || !doc.signature_status) return null;

    const statusConfig = {
      pending: { label: "En attente", color: "bg-amber-100 text-amber-800", icon: Clock },
      completed: { label: "Signé", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
      cancelled: { label: "Annulé", color: "bg-red-100 text-red-800", icon: XCircle }
    };

    const config = statusConfig[doc.signature_status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
      <Badge className={`${config.color} flex items-center space-x-1`}>
        <IconComponent className="w-3 h-3" />
        <span>{config.label}</span>
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Documents - {clientName}</h3>
          <p className="text-sm text-gray-600">{documents.length} document(s)</p>
        </div>
        
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="upload-document-btn">
              <Upload className="w-4 h-4 mr-2" />
              Ajouter Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Ajouter des documents</DialogTitle>
              <DialogDescription>
                Glissez-déposez ou sélectionnez plusieurs fichiers à télécharger
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label htmlFor="file-type">Type de document</Label>
                <Select 
                  value={uploadData.file_type} 
                  onValueChange={(value) => setUploadData({...uploadData, file_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(documentTypes).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Drag & Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-emerald-600' : 'text-gray-400'}`} />
                <p className="text-sm text-gray-600 mb-2">
                  Glissez-déposez vos fichiers ici
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  ou
                </p>
                <label htmlFor="file-input" className="cursor-pointer">
                  <div className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                    <FileText className="w-4 h-4 mr-2" />
                    Parcourir les fichiers
                  </div>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-4">
                  Tous types de fichiers acceptés
                </p>
              </div>

              {/* Files Preview List */}
              {uploadData.files.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  <Label>Fichiers sélectionnés ({uploadData.files.length})</Label>
                  {uploadData.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {uploadProgress[index] !== undefined && (
                          <span className="text-xs text-emerald-600">{uploadProgress[index]}%</span>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          disabled={uploadProgress[index] !== undefined}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => {
                  setShowUploadDialog(false);
                  setUploadData({ files: [], file_type: "general" });
                }}>
                  Annuler
                </Button>
                <Button type="submit" disabled={uploadData.files.length === 0}>
                  Télécharger {uploadData.files.length > 0 && `(${uploadData.files.length})`}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">Aucun document pour ce client</p>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Ajouter le premier document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {documents.map((doc) => (
            <Card key={doc.id} className="hover-lift">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-gray-900">{doc.original_filename}</h4>
                        <p className="text-sm text-gray-500">
                          {documentTypes[doc.file_type] || doc.file_type} • {formatFileSize(doc.file_size)}
                        </p>
                        <p className="text-xs text-gray-400">
                          Téléchargé le {new Date(doc.upload_date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {getSignatureStatus(doc.id)}
                    
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(doc)}
                        title="Prévisualiser"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc.id, doc.original_filename)}
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      
                      {!doc.is_signed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSendForSignature(doc.id)}
                          title="Envoyer pour signature"
                        >
                          <PenTool className="w-4 h-4" />
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(doc.id)}
                        title="Supprimer"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <Dialog open={!!previewDocument} onOpenChange={handleClosePreview}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Prévisualisation - {previewDocument.filename}</DialogTitle>
              <DialogDescription>
                {previewDocument.isSupported 
                  ? 'Utilisez les boutons ci-dessous pour zoomer/dézoomer' 
                  : 'Ce format de fichier ne peut pas être prévisualisé'}
              </DialogDescription>
            </DialogHeader>
            
            {/* Zoom Controls */}
            {previewDocument.isSupported && (
              <div className="flex items-center justify-center space-x-2 py-2 border-b">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 50}
                  title="Dézoomer"
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium min-w-[60px] text-center">
                  {zoomLevel}%
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 200}
                  title="Zoomer"
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetZoom}
                  title="Réinitialiser le zoom"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Preview Content */}
            <div className="flex-1 overflow-auto bg-gray-100 rounded-md p-4">
              {!previewDocument.isSupported ? (
                // Unsupported Format Message
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      Prévisualisation non pris en charge pour ce format
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Veuillez télécharger le fichier pour le consulter
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => handleDownload(previewDocument.id, previewDocument.filename)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger le fichier
                    </Button>
                  </div>
                </div>
              ) : loadingPreview ? (
                // Loading State
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                    <p className="text-sm text-gray-600">Chargement du document...</p>
                  </div>
                </div>
              ) : previewUrl && previewDocument.fileType === 'pdf' ? (
                // PDF Preview
                <div 
                  className="flex items-center justify-center min-h-[600px] bg-white rounded"
                  style={{
                    transform: `scale(${zoomLevel / 100})`,
                    transformOrigin: 'top center',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                >
                  <embed
                    src={previewUrl}
                    type="application/pdf"
                    width="100%"
                    height="600px"
                    className="rounded"
                  />
                </div>
              ) : previewUrl && previewDocument.fileType === 'image' ? (
                // Image Preview
                <div className="flex items-center justify-center min-h-[400px]">
                  <img
                    src={previewUrl}
                    alt={previewDocument.filename}
                    style={{
                      maxWidth: `${zoomLevel}%`,
                      height: 'auto',
                      transition: 'max-width 0.2s ease-in-out'
                    }}
                    className="rounded shadow-lg"
                  />
                </div>
              ) : (
                // Error State
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <XCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      Impossible de charger le document
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Une erreur est survenue lors du chargement
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => handleDownload(previewDocument.id, previewDocument.filename)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger le fichier
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleClosePreview}
              >
                Fermer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Signature Requests */}
      {signatureRequests.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-md font-semibold">Demandes de Signature</h4>
          {signatureRequests.map((request) => (
            <Card key={request.id} className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Signature en cours</p>
                    <p className="text-sm text-gray-600">
                      {request.client_name} • {request.signature_provider}
                    </p>
                    <p className="text-xs text-gray-500">
                      Envoyé le {new Date(request.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <Badge className={
                    request.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                    request.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                    'bg-red-100 text-red-800'
                  }>
                    {request.status === 'completed' ? 'Signé' :
                     request.status === 'pending' ? 'En attente' : 'Erreur'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentManager;